/**
 * UI-preference persistence — non-sensitive client-side flags.
 *
 * Stored in **sessionStorage** (per-tab, cleared on tab close) rather
 * than localStorage. These are UX-only preferences (mode, demo tenant,
 * sidebar collapse) — no tokens, no PII, nothing that survives sign-out.
 *
 * The narrow surface here keeps consumers (`TenantModeContext`,
 * `AppShell`) from touching the storage API directly, which makes the
 * security boundary explicit and the persistence trivially mockable
 * for tests.
 */
import { logger } from "@/lib/logger";

const KEYS = {
  mode: "algos.tenant.mode.v1",
  demoTenantId: "algos.tenant.demoId.v1",
  sidebarCollapsed: "algos.sidebar.collapsed",
};

const safeRead = (key) => {
  try {
    if (typeof window === "undefined") return null;
    // SECURITY: sessionStorage is intentional — this module only persists
    // UX preferences (mode, demo tenant id, sidebar collapse). No tokens,
    // no PII, no auth state. See file header.
    return sessionStorage.getItem(key);
  } catch (err) {
    logger.warn(`[ui-prefs] read failed for ${key}`, err);
    return null;
  }
};

const safeWrite = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    // SECURITY: sessionStorage is intentional — UX preferences only,
    // never tokens or PII. See file header for the security boundary.
    sessionStorage.setItem(key, value);
  } catch (err) {
    logger.warn(`[ui-prefs] write failed for ${key}`, err);
  }
};

export const readMode = () => (safeRead(KEYS.mode) === "customer" ? "customer" : "demo");
export const writeMode = (mode) => safeWrite(KEYS.mode, mode);

export const readDemoTenantId = (allowedIds, defaultId) => {
  const stored = safeRead(KEYS.demoTenantId);
  return stored && allowedIds.has(stored) ? stored : defaultId;
};
export const writeDemoTenantId = (id) => safeWrite(KEYS.demoTenantId, id);

export const readSidebarCollapsed = () => safeRead(KEYS.sidebarCollapsed) === "1";
export const writeSidebarCollapsed = (collapsed) =>
  safeWrite(KEYS.sidebarCollapsed, collapsed ? "1" : "0");
